/**
 * Excess Inventory Report Component
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { ReportResponse, ExcessInventoryItem } from '../../types/reports';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

const ExcessInventoryReport: React.FC = () => {
  const { fmtCurrencyPrecise, fmtCurrency } = useCurrencyFormat();
  const [threshold, setThreshold] = useState(60);
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Fetch stores
  const { data: stores } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const response = await api.getStores();
      return response.data;
    },
  });

  // Fetch products for categories
  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await api.getProducts();
      return response.data;
    },
  });

  // Get unique categories
  const categories: string[] = Array.from(
    new Set(products?.map((p: any) => p.category) || [])
  ) as string[];

  // Fetch report data
  const {
    data: reportData,
    isLoading,
    error,
    refetch,
  } = useQuery<ReportResponse<ExcessInventoryItem>>({
    queryKey: ['excessInventoryReport', threshold, selectedStore, selectedCategory],
    queryFn: async () => {
      const response = await api.getExcessInventoryReport({
        threshold,
        store_id: selectedStore || undefined,
        category: selectedCategory || undefined,
      });
      return response.data;
    },
  });

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const response =
        format === 'csv'
          ? await api.exportReportCSV('excess_inventory', {
              threshold,
              store_id: selectedStore || undefined,
              category: selectedCategory || undefined,
            })
          : await api.exportReportJSON('excess_inventory', {
              threshold,
              store_id: selectedStore || undefined,
              category: selectedCategory || undefined,
            });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `excess_inventory_${new Date().toISOString().split('T')[0]}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Export error - silently handled
    }
  };

  // Calculate total excess value
  const totalExcessValue = reportData?.data.reduce(
    (sum, item) => sum + item.excess_value_estimate,
    0
  ) || 0;

  const totalExcessQuantity = reportData?.data.reduce(
    (sum, item) => sum + item.excess_quantity,
    0
  ) || 0;

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            type="number"
            label="Days of Supply Threshold"
            value={threshold}
            onChange={(e) => setThreshold(parseInt(e.target.value) || 60)}
            helperText="Items with more than this many days of supply"
          />
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Store Filter</InputLabel>
            <Select
              value={selectedStore}
              label="Store Filter"
              onChange={(e) => setSelectedStore(e.target.value)}
            >
              <MenuItem value="">All Stores</MenuItem>
              {stores?.map((store: any) => (
                <MenuItem key={store.store_id} value={store.store_id}>
                  {store.store_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Category Filter</InputLabel>
            <Select
              value={selectedCategory}
              label="Category Filter"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('csv')}
            >
              Export
            </Button>
          </Box>
        </Grid>
      </Grid>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading report: {(error as Error).message}
        </Alert>
      )}

      {reportData && (
        <>
          {reportData.data.length > 0 && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Excess Inventory Items: {reportData.metadata.record_count}</strong>
              </Typography>
              <Typography variant="body2">
                Total Excess Quantity: <strong>{totalExcessQuantity} units</strong>
              </Typography>
              <Typography variant="body2">
                Estimated Value: <strong>{fmtCurrencyPrecise(totalExcessValue)}</strong>
              </Typography>
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Generated: {new Date(reportData.metadata.generated_at).toLocaleString()}
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Store</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Item Status</TableCell>
                  <TableCell align="right">Current Stock</TableCell>
                  <TableCell align="right">Max Stock</TableCell>
                  <TableCell align="right">Avg Daily Sales</TableCell>
                  <TableCell align="right">Days of Supply</TableCell>
                  <TableCell align="right">Excess Qty</TableCell>
                  <TableCell align="right">Est. Value</TableCell>
                  <TableCell>Stock Health</TableCell>
                  <TableCell>Recommendation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      <Alert severity="success">
                        No excess inventory detected - Inventory levels are optimal!
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  reportData.data.map((item: ExcessInventoryItem, index: number) => (
                    <TableRow key={index}>
                      <TableCell>{item.store_name}</TableCell>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell>{item.sku}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell sx={{ py: '6px', px: '8px' }}>
                        {(item as any).lifecycle_stage ? (
                          <Chip
                            label={(item as any).lifecycle_stage}
                            size="small"
                            color={
                              (item as any).lifecycle_stage === 'NEW' ? 'info' :
                              (item as any).lifecycle_stage === 'GROWTH' ? 'success' :
                              (item as any).lifecycle_stage === 'DECLINING' ? 'warning' :
                              ((item as any).lifecycle_stage === 'PHASE_OUT' || (item as any).lifecycle_stage === 'DISCONTINUED') ? 'error' :
                              'default'
                            }
                            variant="outlined"
                            sx={{ fontSize: '10px', height: 20 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{item.current_stock}</TableCell>
                      <TableCell align="right">
                        {item.max_stock || 'N/A'}
                      </TableCell>
                      <TableCell align="right">
                        {Math.ceil(Number(item.avg_daily_sales) || 0)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          color={item.days_of_supply > 90 ? 'error' : 'warning'}
                          sx={{ fontWeight: 'bold' }}
                        >
                          {item.days_of_supply.toFixed(0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <strong>{item.excess_quantity}</strong>
                      </TableCell>
                      <TableCell align="right">
                        {fmtCurrencyPrecise(item.excess_value_estimate)}
                      </TableCell>
                      <TableCell>
                        {item.slow_moving && (
                          <Chip
                            label="Slow Moving"
                            color="warning"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {item.recommendation.replace('_', ' ').toUpperCase()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default ExcessInventoryReport;
